// What the call looked like when its person pressed Leave, sent to the room
// as a "leaving" drop report (worker/src/drops.ts). A hang-up followed by a
// rejoin is usually someone fixing a call that froze or went silent, and the
// server only sees the hang-up (QUEUE row 337), so the client says what it
// saw: the media connection's state, whether the others' audio and video
// were arriving, its own mic and camera, the network, and the last few
// minutes of drops and state changes.
//
// Built synchronously, since Leave closes the socket at once.

export interface RecentEvent {
  readonly at: number;
  readonly e: string;
}

const KEEP_MS = 10 * 60 * 1000;
const KEEP_COUNT = 16;

/** Adds an event to the recent list, keeping the last ten minutes and at
 * most sixteen entries. */
export function noteRecent(list: RecentEvent[], e: string, now: number = Date.now()): void {
  list.push({ at: now, e });
  while (list.length > KEEP_COUNT || (list.length > 0 && now - list[0]!.at > KEEP_MS)) list.shift();
}

/** A local track in a word: none, off (switched off by its person), ended,
 * silent (live but its source sends nothing), or live. */
export function trackWord(track: MediaStreamTrack | null | undefined): string {
  if (!track) return "none";
  if (track.readyState === "ended") return "ended";
  if (!track.enabled) return "off";
  if (track.muted) return "silent";
  return "live";
}

export interface LeaveInputs {
  readonly pc: RTCPeerConnection | null;
  readonly mic: MediaStreamTrack | null;
  readonly camera: MediaStreamTrack | null;
  readonly recent: readonly RecentEvent[];
  readonly joinedAt: number | null;
  /** The last sample of what each camera arriving here looked like, as
   * "f 1280x720 30fps" per camera (QUEUE row 340: a guest sharp in the
   * recording and fuzzy for the host left no trace of what the host got). */
  readonly videoIn?: string;
  readonly now?: number;
}

export function leaveContext(input: LeaveInputs): Record<string, unknown> {
  const now = input.now ?? Date.now();
  const out: Record<string, unknown> = {};
  if (input.pc) {
    out.media = input.pc.connectionState;
    out.ice = input.pc.iceConnectionState;
    // A remote track that is muted is one whose packets stopped arriving,
    // which is what a frozen tile or a silent person looks like from here.
    let audio = 0;
    let audioStalled = 0;
    let video = 0;
    let videoStalled = 0;
    // Only sections that receive: a sendonly transceiver (our own mic and
    // camera) still has a receiver whose track is always muted, which made
    // every report count one stalled audio track (QUEUE row 413).
    for (const t of input.pc.getTransceivers()) {
      if (t.currentDirection !== "recvonly" && t.currentDirection !== "sendrecv") continue;
      const track = t.receiver.track;
      if (!track || track.readyState === "ended") continue;
      if (track.kind === "audio") {
        audio++;
        if (track.muted) audioStalled++;
      } else {
        video++;
        if (track.muted) videoStalled++;
      }
    }
    out.remoteAudio = audio;
    out.remoteAudioStalled = audioStalled;
    out.remoteVideo = video;
    out.remoteVideoStalled = videoStalled;
  } else {
    out.media = "none";
  }
  out.mic = trackWord(input.mic);
  out.camera = trackWord(input.camera);
  if (typeof navigator !== "undefined") {
    out.online = navigator.onLine;
    const connection = (navigator as Navigator & { connection?: { effectiveType?: string; type?: string } }).connection;
    if (connection?.effectiveType) out.net = connection.effectiveType;
    if (connection?.type) out.netType = connection.type;
  }
  if (typeof document !== "undefined") out.hidden = document.visibilityState === "hidden";
  if (input.videoIn) out.videoIn = input.videoIn;
  if (input.joinedAt) out.inCallSecs = Math.round((now - input.joinedAt) / 1000);
  out.recent = input.recent.map((r) => ({ s: Math.round((now - r.at) / 1000), e: r.e }));
  return out;
}
